import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useMediaQuery} from "@vueuse/core";
import useSwr from "@/core/composables/swr/useSwr";
import {shellUpdateApi} from "@/container";
import {cacheKey} from "@/lib/cache";
import {supportsShellUpdate} from "@/core/services/shellCapabilities";
import {ShellUpdateOffer, ShellUpdateStatus} from "@/shell-update/interfaces";

/**
 * In dev, `localStorage.setItem('forceShellUpdateOffer', '3.4.9')` offers that version without asking the
 * API, and scheduling it stays in memory: the button and the dialog can be tried without the update server.
 */
const forcedTarget = (): string | null =>
    import.meta.env.DEV ? localStorage.getItem('forceShellUpdateOffer') : null;

/**
 * Connect stays open for days at the counter: the offer is read again on this
 * interval, so an update opened on the server, or scheduled from another
 * workstation of the pharmacy, shows without a restart.
 */
const OFFER_REFRESH_MS = 30 * 60 * 1000;

/**
 * The update server reads the scheduled updates again every 30 s: the check
 * made right after scheduling usually comes before it knows of this one, so a
 * second check follows once it does. The shell ignores a check while it is
 * already downloading or holds the update.
 */
const SCHEDULED_RECHECK_MS = 45 * 1000;

/**
 * The offer made while the dev override is set.
 */
const forcedOffer = (target: string, scheduled: boolean): ShellUpdateOffer => ({
    available: true,
    target_version: target,
    scheduled_until: scheduled ? new Date(Date.now() + 72 * 3600 * 1000).toISOString() : null,
});

/**
 * The shell update of this Connect: the offer made by the API, its scheduling
 * for the whole pharmacy and, on shells that report it, the download itself.
 *
 * The update server only lets a pharmacy's workstations through once the
 * update is scheduled. Older shells then pick it up on their next periodic
 * check (one to two hours) and ask for the restart through their native
 * dialog; newer ones check right away and leave the restart to the renderer.
 */
const useShellUpdate = () => {
    const version = ref<string | null>(null);
    const status = ref<ShellUpdateStatus | null>(null);
    const drivesUpdate = supportsShellUpdate();

    const forced = forcedTarget();

    const key = computed(() => version.value ? cacheKey('shell-update-offer', {version: version.value, forced}) : null);

    const {data: offer, mutate} = useSwr(
        () => key.value,
        () => forced
            ? Promise.resolve(forcedOffer(forced, false))
            : shellUpdateApi().offer(version.value as string),
        {ttl: 1000 * 60 * 60, refreshInterval: forced ? 0 : OFFER_REFRESH_MS}
    );

    /**
     * The top bar hides the version pill below the md breakpoint, and the main
     * window can be narrower than that: the renderer only asks for the restart
     * while its link can be seen, the shell's native dialog does otherwise.
     */
    const linkFits = useMediaQuery('(min-width: 768px)');

    let stopListening: (() => void) | undefined;
    let recheck: ReturnType<typeof setTimeout> | undefined;

    onMounted(async () => {
        if (!window.ipcRenderer) {
            return;
        }

        version.value = await window.ipcRenderer.invoke('electron-version') as string;

        if (drivesUpdate) {
            watch(linkFits, (fits) => {
                window.ipcRenderer.setShellUpdatePromptOwner?.(fits ? 'renderer' : 'shell');
            }, {immediate: true});
            stopListening = window.ipcRenderer.onShellUpdateStatus?.((next) => {
                status.value = next;
            });
            status.value = await window.ipcRenderer.getShellUpdateStatus?.() ?? null;
        }
    });

    onUnmounted(() => {
        stopListening?.();

        if (drivesUpdate) {
            window.ipcRenderer?.setShellUpdatePromptOwner?.('shell');
        }
    });

    /** The update is downloaded and waits for the restart. */
    const isDownloaded = computed(() => status.value?.state === 'downloaded');

    /** The update is scheduled for the pharmacy. */
    const isScheduled = computed(() => !!offer.value?.scheduled_until);

    /** Whether anything should be shown at all. */
    const isVisible = computed(() => isDownloaded.value || !!offer.value?.available);

    /**
     * Schedule the update for every workstation of the pharmacy, and start
     * this workstation's download as soon as the update server lets it
     * through, when the shell allows it.
     */
    const schedule = async () => {
        if (!version.value) {
            return;
        }

        const scheduled = forced
            ? forcedOffer(forced, true)
            : await shellUpdateApi().schedule(version.value);

        await mutate(() => Promise.resolve(scheduled));

        if (drivesUpdate) {
            await window.ipcRenderer.checkForShellUpdate?.();

            clearTimeout(recheck);
            recheck = setTimeout(() => window.ipcRenderer.checkForShellUpdate?.(), SCHEDULED_RECHECK_MS);
        }
    };

    /** Restart on the downloaded version. */
    const install = () => {
        window.ipcRenderer?.installShellUpdate?.();
    };

    return {
        offer,
        status,
        isDownloaded,
        isScheduled,
        isVisible,
        schedule,
        install,
    };
};

export default useShellUpdate;
